import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Button, Card, Checkbox } from '@/components/ui'
import { IGooglePaySettings } from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getGooglePaySettings,
  updateGooglePaySettings,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'

interface IGooglePayValues {
  googlePayEnabled: boolean
}

export const GooglePay: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [googlePaySettings, setGooglePaySettings] =
    useState<IGooglePaySettings>()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getGooglePaySettings()
        if (resp.status === 200) {
          setGooglePaySettings(resp.data)
          setValue('googlePayEnabled', resp.data.googlePayEnabled)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    googlePayEnabled: yup.boolean().required(),
  })

  const {
    handleSubmit,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm<IGooglePayValues>({
    mode: 'onChange',
    resolver: yupResolver<IGooglePayValues>(schema),
    defaultValues: {
      googlePayEnabled: false,
    },
  })

  const submit = async (values: IGooglePayValues) => {
    try {
      const { status } = await updateGooglePaySettings(values)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (googlePaySettings) {
      setValue('googlePayEnabled', googlePaySettings.googlePayEnabled)
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-google-pay-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-[28px]"
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">Google Pay</h3>
          <FormGroup
            label="Enable Google Pay"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'googlePayEnabled',
            }}
          >
            <Controller
              name="googlePayEnabled"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="googlePayEnabled"
                  ref={field.ref}
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={!hasEditPermission}
                />
              )}
            />
          </FormGroup>
        </form>

        <div className="flex justify-end gap-2 border-t border-monochrome-120 mt-4 [&>*]:w-[154px] pt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-google-pay-form"
            type="submit"
            variant="primary"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
